// multiFileUpload.ts - UPDATED: Confirmation only for multiple files
import { useState, useCallback, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import logger, { Logger } from '../../utils/logger';
import { isImage, isVideo } from '../../utils/helpers';

// ============================================================================
// TYPES (No changes)
// ============================================================================

export interface FileItem {
  id: string;
  file: File;
  type: 'image' | 'video' | 'document';
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
  error?: string;
  uploadedId?: string;
}

export interface UploadAPI {
  uploadMedia: (formData: FormData) => Promise<any>;
  sendMessageToLead: (payload: any) => Promise<any>;
}

export interface ConfirmationModalProps {
  showModal: boolean;
  onConfirmation: () => void;
  toggleModal: () => void;
  message: string;
  title: string;
  confirmText: string;
  cancelText: string;
}

// ============================================================================
// PERFECT FILE TYPE DETECTION (No changes)
// ============================================================================

export const detectFileType = (file: File): 'image' | 'video' | 'document' => {
  const name = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();
  
  logger.log(`🔍 DETECTING FILE TYPE:`, {
    fileName: file.name,
    mimeType: file.type,
    size: file.size
  });

  // WhatsApp supported types
  if (mimeType.includes('image') || /\.(jpg|jpeg|png)$/i.test(name)) {
    logger.log(`📸 IMAGE DETECTED: ${file.name} (${mimeType})`);
    return 'image';
  }
  
  if (mimeType.includes('video') || /\.(mp4|3gp)$/i.test(name)) {
    logger.log(`🎥 VIDEO DETECTED: ${file.name} (${mimeType})`);
    return 'video';
  }
  
  logger.log(`📄 DOCUMENT DETECTED: ${file.name} (${mimeType})`);
  return 'document';
};

// ============================================================================
// VALIDATION FUNCTIONS (No changes)
// ============================================================================

export const isWhatsAppSupported = (file: File): boolean => {
  const type = detectFileType(file);
  const name = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();
  
  // Image validation
  if (type === 'image') {
    const validImage = (mimeType === 'image/jpeg' || mimeType === 'image/png' || 
                       name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png'));
    logger.log(`✅ IMAGE WHATSAPP CHECK: ${file.name} - ${validImage ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
    return validImage;
  }
  
  // Video validation
  if (type === 'video') {
    const validVideo = (mimeType === 'video/mp4' || mimeType === 'video/3gpp' || 
                       name.endsWith('.mp4') || name.endsWith('.3gp'));
    logger.log(`✅ VIDEO WHATSAPP CHECK: ${file.name} - ${validVideo ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
    return validVideo;
  }
  
  // Document validation (PDF only for WhatsApp)
  if (type === 'document') {
    const validDoc = (mimeType === 'application/pdf' || name.endsWith('.pdf'));
    logger.log(`✅ DOCUMENT WHATSAPP CHECK: ${file.name} - ${validDoc ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
    return validDoc;
  }
  
  logger.log(`❌ WHATSAPP NOT SUPPORTED: ${file.name} (${type})`);
  return false;
};

export const validateFile = (file: File): { valid: boolean; error?: string } => {
  logger.log(`🔍 VALIDATING FILE: ${file.name}`);
  
  // Basic checks
  if (!file || !file.name) {
    logger.log(`❌ VALIDATION FAILED: Invalid file object`);
    return { valid: false, error: 'Invalid file' };
  }
  
  if (file.size === 0) {
    logger.log(`❌ VALIDATION FAILED: Empty file - ${file.name}`);
    return { valid: false, error: 'File is empty' };
  }
  
  // WhatsApp support check
  if (!isWhatsAppSupported(file)) {
    logger.log(`❌ VALIDATION FAILED: File type not supported - ${file.name}`);
    return { valid: false, error: 'File type not supported. Use JPG, PNG, MP4, 3GP, or PDF' };
  }
  
  // Size limits
  const type = detectFileType(file);
  const sizeLimits = {
    image: 5 * 1024 * 1024,    // 5MB
    video: 16 * 1024 * 1024,   // 16MB  
    document: 16 * 1024 * 1024 // 16MB
  };
  
  const limit = sizeLimits[type];
  if (file.size > limit) {
    const limitMB = Math.round(limit / (1024 * 1024));
    logger.log(`❌ VALIDATION FAILED: File too large - ${file.name} (${file.size} bytes > ${limit} bytes)`);
    return { valid: false, error: `File too large. Maximum ${limitMB}MB for ${type}` };
  }
  
  logger.log(`✅ VALIDATION PASSED: ${file.name} (${type}, ${file.size} bytes)`);
  return { valid: true };
};

// ============================================================================
// 🔥 UPDATED: MAIN UPLOAD HOOK - Confirmation only for multiple files
// ============================================================================

export const useMultiFileUpload = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const apiRef = useRef<UploadAPI | null>(null);
  const conversationRef = useRef<any>(null);
  const phoneIdRef = useRef<string>('');

  // Anti-infinite loop protection
  const isProcessingRef = useRef(false);
  const processedIdsRef = useRef<Set<string>>(new Set());

  // Auto-clear timer ref
  const autoClearTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 🔥 UPDATED: Confirmation modal state - only for multiple files
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFilesForConfirmation, setPendingFilesForConfirmation] = useState<FileItem[]>([]);
  const confirmationResolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  // 🔥 NEW: Multiple files confirmation helper
  const showMultipleFilesConfirmation = useCallback((files: FileItem[]): Promise<boolean> => {
    return new Promise((resolve) => {
      logger.log(`🤔 SHOWING MULTIPLE FILES CONFIRMATION: ${files.length} files`);
      setPendingFilesForConfirmation(files);
      setShowConfirmModal(true);
      confirmationResolveRef.current = resolve;
    });
  }, []);

  // 🔥 UPDATED: Modal handlers
  const handleConfirmSend = useCallback(() => {
    logger.log(`✅ CONFIRMED: User confirmed sending ${pendingFilesForConfirmation.length} files`);
    setShowConfirmModal(false);
    setPendingFilesForConfirmation([]);
    if (confirmationResolveRef.current) {
      confirmationResolveRef.current(true);
      confirmationResolveRef.current = null;
    }
  }, [pendingFilesForConfirmation]);

  const handleCancelSend = useCallback(() => {
    logger.log(`❌ CANCELLED: User cancelled sending ${pendingFilesForConfirmation.length} files`);
    setShowConfirmModal(false);
    setPendingFilesForConfirmation([]);
    if (confirmationResolveRef.current) {
      confirmationResolveRef.current(false);
      confirmationResolveRef.current = null;
    }
  }, [pendingFilesForConfirmation]);

  // Setup API (No changes)
  const setupAPI = useCallback(async () => {
    try {
      const { uploadMedia, sendMessageToLead } = await import("../../services/whatsapp");
      apiRef.current = { uploadMedia, sendMessageToLead };
      logger.log('📡 API SETUP: WhatsApp services loaded');
    } catch (error) {
      console.error('❌ API SETUP FAILED:', error);
    }
  }, []);

  // Set conversation (No changes)
  const setConversation = useCallback((conversation: any, phoneId: string) => {
    conversationRef.current = conversation;
    phoneIdRef.current = phoneId;
    logger.log('💬 CONVERSATION SET:', { 
      id: conversation?.id, 
      phone: conversation?.mobile,
      phoneId 
    });
  }, []);

  // Add files (No changes)
  const addFiles = useCallback((newFiles: File[]) => {
    logger.log(`📂 ADDING FILES: ${newFiles.length} files dropped`);
    
    const validFiles: FileItem[] = [];
    let errorCount = 0;

    newFiles.forEach((file, index) => {
      logger.log(`\n--- PROCESSING FILE ${index + 1}/${newFiles.length} ---`);
      
      const validation = validateFile(file);
      if (!validation.valid) {
        logger.log(`❌ FILE REJECTED: ${file.name} - ${validation.error}`);
        toast.error(`${file.name}: ${validation.error}`);
        errorCount++;
        return;
      }

      const fileItem: FileItem = {
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        type: detectFileType(file),
        status: 'pending',
        progress: 0
      };

      validFiles.push(fileItem);
      logger.log(`✅ FILE ACCEPTED: ${file.name} (ID: ${fileItem.id})`);
    });

    logger.log(`\n📊 PROCESSING COMPLETE: ${validFiles.length} accepted, ${errorCount} rejected`);

    if (validFiles.length > 0) {
      setFiles(prev => {
        const updated = [...prev, ...validFiles];
        logger.log(`📋 QUEUE UPDATED: ${updated.length} total files in queue`);
        return updated;
      });
      toast.success(`${validFiles.length} file(s) added to queue`);
    }
  }, []);

  // Remove file (No changes)
  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      const updated = prev.filter(f => f.id !== fileId);
      logger.log(`🗑️ FILE REMOVED: ${file?.file.name} (ID: ${fileId})`);
      logger.log(`📋 QUEUE UPDATED: ${updated.length} files remaining`);
      
      // Remove from processed set when file is removed
      processedIdsRef.current.delete(fileId);
      
      toast.success(`Removed ${file?.file.name}`);
      return updated;
    });
  }, []);

  // Auto-cleanup (No changes)
  const autoCleanup = useCallback(() => {
    logger.log('🧹 AUTO-CLEANUP: Checking for completed files');
    
    setFiles(prev => {
      const completedFiles = prev.filter(f => f.status === 'completed');
      const updated = prev.filter(f => f.status !== 'completed');
      
      if (completedFiles.length > 0) {
        logger.log(`🧹 AUTO-CLEANUP: Removing ${completedFiles.length} completed files`);
        
        // Remove completed IDs from processed set
        completedFiles.forEach(file => {
          processedIdsRef.current.delete(file.id);
        });
        
        toast.success(`✨ Cleared ${completedFiles.length} sent file(s)`);
        return updated;
      }
      
      return prev;
    });
  }, []);

  // Clear completed files manually
  const clearCompleted = useCallback(() => {
    autoCleanup();
  }, [autoCleanup]);

  // 🔥 UPDATED: Process queue with batch confirmation for multiple files
  const processQueue = useCallback(async () => {
    // Prevent multiple simultaneous processing
    if (isProcessingRef.current) {
      logger.log('⚠️ UPLOAD ALREADY IN PROGRESS - SKIPPING');
      return;
    }

    // Find ALL pending files that haven't been processed
    const pendingFiles = files.filter(f => 
      f.status === 'pending' && 
      !processedIdsRef.current.has(f.id)
    );

    if (pendingFiles.length === 0) {
      logger.log('ℹ️ NO NEW PENDING FILES TO PROCESS');
      
      // Check if all uploads are complete for auto-cleanup
      const allCompleted = files.length > 0 && files.every(f => f.status === 'completed');
      if (allCompleted) {
        logger.log('🎉 ALL FILES COMPLETED - Starting auto-cleanup in 3 seconds');
        
        // Clear any existing timer
        if (autoClearTimerRef.current) {
          clearTimeout(autoClearTimerRef.current);
        }
        
        // Set new auto-clear timer
        autoClearTimerRef.current = setTimeout(() => {
          autoCleanup();
        }, 3000);
      }
      
      return;
    }

    if (!apiRef.current) {
      logger.log('❌ UPLOAD FAILED: API not ready');
      toast.error('Upload service not available');
      return;
    }

    if (!conversationRef.current) {
      logger.log('❌ UPLOAD FAILED: No conversation selected');
      toast.error('No conversation selected');
      return;
    }

    // 🔥 NEW: Only show confirmation for multiple files
    if (pendingFiles.length > 1) {
      logger.log(`🤔 MULTIPLE FILES DETECTED: ${pendingFiles.length} files - showing confirmation`);
      
      const shouldSend = await showMultipleFilesConfirmation(pendingFiles);
      if (!shouldSend) {
        logger.log(`❌ BATCH SEND CANCELLED: User cancelled sending ${pendingFiles.length} files`);
        
        // Mark all pending files as cancelled
        setFiles(prev => prev.map(f => 
          pendingFiles.some(pf => pf.id === f.id)
            ? { ...f, status: 'failed' as const, error: 'Cancelled by user' }
            : f
        ));
        
        return;
      }
    } else {
      logger.log(`📤 SINGLE FILE: ${pendingFiles[0].file.name} - uploading directly without confirmation`);
    }

    // Mark as processing
    isProcessingRef.current = true;
    setUploading(true);

    // Process files one by one
    for (const pendingFile of pendingFiles) {
      // Mark as processing
      processedIdsRef.current.add(pendingFile.id);

      logger.log(`\n🚀 STARTING UPLOAD: ${pendingFile.file.name} (ID: ${pendingFile.id})`);

      try {
        // Update status
        setFiles(prev => prev.map(f => 
          f.id === pendingFile.id 
            ? { ...f, status: 'uploading' as const, progress: 0 }
            : f
        ));

        // Prepare upload
        const formData = new FormData();
        formData.append('files', pendingFile.file);
        logger.log(`📤 UPLOADING: ${pendingFile.file.name} (${pendingFile.file.size} bytes)`);

        // Progress simulation
        const progressInterval = setInterval(() => {
          setFiles(prev => prev.map(f => 
            f.id === pendingFile.id && f.status === 'uploading'
              ? { ...f, progress: Math.min(f.progress + 15, 90) }
              : f
          ));
        }, 300);

        // Upload
        const uploadResponse = await apiRef.current.uploadMedia(formData);
        clearInterval(progressInterval);

        if (!uploadResponse?.data?.data?.[0]?.uploadedId) {
          throw new Error('No upload ID received');
        }

        const uploadedId = uploadResponse.data.data[0].uploadedId;
        logger.log(`✅ UPLOAD SUCCESS: ${pendingFile.file.name} (ID: ${uploadedId})`);

        // Update progress
        setFiles(prev => prev.map(f => 
          f.id === pendingFile.id 
            ? { ...f, progress: 95, uploadedId }
            : f
        ));

        // Send message
        const payload: any = {
          phone: conversationRef.current.mobile,
          phoneId: phoneIdRef.current,
          type: pendingFile.type,
          [pendingFile.type]: { id: uploadedId }
        };

        logger.log(`📨 SENDING MESSAGE: ${pendingFile.file.name}`);
        await apiRef.current.sendMessageToLead(payload);

        // Mark complete
        setFiles(prev => prev.map(f => 
          f.id === pendingFile.id 
            ? { ...f, status: 'completed' as const, progress: 100 }
            : f
        ));

        // Auto-clear completed files after delay
setTimeout(() => {
  setFiles(prev => prev.filter(f => f.id !== pendingFile.id));
  
  // Check if queue is now empty and show success
  setFiles(current => {
    if (current.length === 0) {
      toast.success('🎉 All files uploaded successfully!');
    }
    return current;
  });
}, 2000); // 2 second delay

        logger.log(`✅ MESSAGE SENT: ${pendingFile.file.name}`);
        toast.success(`✅ ${pendingFile.file.name} sent successfully`);

        // Small delay between uploads
        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error: any) {
        console.error(`❌ UPLOAD FAILED: ${pendingFile.file.name} -`, error);
        
        // Remove from processed set on failure so it can be retried
        processedIdsRef.current.delete(pendingFile.id);
        
        setFiles(prev => prev.map(f => 
          f.id === pendingFile.id 
            ? { ...f, status: 'failed' as const, error: error.message || 'Upload failed' }
            : f
        ));

        toast.error(`❌ Failed to send ${pendingFile.file.name}`);
      }
    }

    // Always reset processing flag
    isProcessingRef.current = false;
    setUploading(false);
  }, [files, autoCleanup, showMultipleFilesConfirmation]);

  // Auto-process queue (No changes)
  useEffect(() => {
    // Only check for truly new pending files
    const newPendingFiles = files.filter(f => 
      f.status === 'pending' && 
      !processedIdsRef.current.has(f.id)
    );

    if (newPendingFiles.length > 0 && !isProcessingRef.current) {
      logger.log(`🤖 AUTO-PROCESSING: ${newPendingFiles.length} new pending files found`);
      
      // Use setTimeout to prevent rapid firing
      const timer = setTimeout(() => {
        if (!isProcessingRef.current) {
          processQueue();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [files.length, processQueue]);

  // Setup API on mount (No changes)
  useEffect(() => {
    setupAPI();
  }, [setupAPI]);

  // Cleanup on unmount (No changes)
  useEffect(() => {
    return () => {
      processedIdsRef.current.clear();
      isProcessingRef.current = false;
      
      if (autoClearTimerRef.current) {
        clearTimeout(autoClearTimerRef.current);
      }
    };
  }, []);

  // Format file size for display (No changes)
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  // 🔥 UPDATED: Return object with updated confirmation modal props
  return {
    files,
    uploading,
    addFiles,
    removeFile,
    clearCompleted,
    processQueue,
    setConversation,
    formatFileSize,
    queueCount: files.length,
    pendingCount: files.filter(f => f.status === 'pending').length,
    completedCount: files.filter(f => f.status === 'completed').length,
    failedCount: files.filter(f => f.status === 'failed').length,
    
    // 🔥 UPDATED: Confirmation modal props for multiple files
    confirmationModal: {
      showModal: showConfirmModal,
      onConfirmation: handleConfirmSend,
      toggleModal: handleCancelSend,
      message: pendingFilesForConfirmation.length > 0 
        ? `Send ${pendingFilesForConfirmation.length} files to WhatsApp?\n\nFiles:\n${pendingFilesForConfirmation.map(f => `• ${f.file.name} (${formatFileSize(f.file.size)})`).join('\n')}\n\nTotal size: ${formatFileSize(pendingFilesForConfirmation.reduce((total, f) => total + f.file.size, 0))}`
        : '',
      title: '📤 Send Multiple Files to WhatsApp',
      confirmText: 'Send All',
      cancelText: 'Cancel'
    }
  };
};